import React from 'react'
import { CheckIcon } from '@heroicons/react/24/solid'

export interface CheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  disabled?: boolean
  className?: string
  id?: string
}

/**
 * Checkbox Component
 *
 * A Tailwind-based checkbox component using design system theme colors:
 * - Unchecked: Thunder grey border (border-thunder-grey)
 * - Checked: Sun border with off-black check icon (border-sun, text-off-black)
 *
 * @param checked - Whether the checkbox is checked
 * @param onChange - Callback when checkbox state changes
 * @param label - Optional label text
 * @param disabled - Whether the checkbox is disabled
 * @param className - Additional CSS classes
 * @param id - HTML id attribute
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onChange,
  label,
  disabled = false,
  className = '',
  id
}) => {
  const handleChange = () => {
    if (!disabled) {
      onChange(!checked)
    }
  }

  const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <div className="relative">
        <input
          type="checkbox"
          id={checkboxId}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only"
        />
        <label
          htmlFor={checkboxId}
          className={`
            flex items-center justify-center w-5 h-5 rounded border-2 cursor-pointer transition-all duration-200
            ${checked ? 'border-sun bg-sun' : 'border-thunder-grey bg-transparent'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-sun-20'}
          `}
        >
          {checked && <CheckIcon className="w-3 h-3 text-off-black" />}
        </label>
      </div>
      {label && (
        <label
          htmlFor={checkboxId}
          className={`
            text-body-2 cursor-pointer select-none
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${checked ? 'text-sun' : 'text-white'}
          `}
        >
          {label}
        </label>
      )}
    </div>
  )
}

export default Checkbox
